import { useEffect, useState, useCallback, useRef } from "react";

import { useMsal } from "@azure/msal-react";
import { ListPlus, Pencil, Trash2, Tag } from "lucide-react";

import { callApi } from "../api/api";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";

export default function LookupManagement() {
  const { instance, accounts } = useMsal();

  /* =========================================
     STATE
  ========================================= */

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");

  const [category, setCategory] = useState("");

  const [page, setPage] = useState(1);

  const [pageSize] = useState(10);

  const [totalRecords, setTotalRecords] = useState(0);

  const [showModal, setShowModal] = useState(false);

  const [isEdit, setIsEdit] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);

  const [toast, setToast] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  const [categories, setCategories] = useState([]);

  const [form, setForm] = useState({
    lookup_category: "document_type",
    lookup_code: "",
    lookup_name: "",
    lookup_description: "",
    sort_order: 1,
    is_active: true,
  });

  const [isNarrowToolbar, setIsNarrowToolbar] = useState(false);
  const [isAddTooltipVisible, setIsAddTooltipVisible] = useState(false);
  const addTooltipTouchTimer = useRef(null);


  useEffect(() => {
    return () => {
      if (addTooltipTouchTimer.current) clearTimeout(addTooltipTouchTimer.current);
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsNarrowToolbar(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const showAddTooltip = () => setIsAddTooltipVisible(true);
  const hideAddTooltip = () => setIsAddTooltipVisible(false);
  const handleAddTooltipTouchStart = () => {
    addTooltipTouchTimer.current = setTimeout(() => setIsAddTooltipVisible(true), 300);
  };
  const handleAddTooltipTouchEnd = () => {
    if (addTooltipTouchTimer.current) clearTimeout(addTooltipTouchTimer.current);
    setTimeout(() => setIsAddTooltipVisible(false), 1200);
  };

  /* =========================================
     TOAST
  ========================================= */

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });

    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  /* =========================================
     LOAD DATA
  ========================================= */

  const loadCategories = useCallback(async () => {
    try {
      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/admin/lookup-categories",
      );

      const data = res?.data?.data || [];

      setCategories(data);

      if (data.length > 0 && !category) {
        setCategory(data[0].lookup_category);
      }
    } catch (err) {
      console.error("Failed to load categories", err);
    }
  }, [instance, accounts, category]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/admin/lookup-values",
        null,
        {
          params: {
            category,
            search,
            page,
            pageSize,
          },
        },
      );

      setRows(res?.data?.data || []);

      setTotalRecords(res?.data?.total || 0);
    } catch (err) {
      console.error(err);

      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, category, search, page, pageSize]);

  useEffect(() => {
    if (accounts.length) {
      loadCategories();
    }
  }, [accounts.length, loadCategories]);

  useEffect(() => {
    if (accounts.length && category) {
      loadData();
    }
  }, [accounts.length, category, loadData]);
  /* =========================================
     RESET
  ========================================= */

  const resetForm = () => {
    setForm({
      lookup_category: category,
      lookup_code: "",
      lookup_name: "",
      lookup_description: "",
      sort_order: 1,
      is_active: true,
    });

    setSelectedRow(null);

    setIsEdit(false);
  };

  /* =========================================
     CREATE
  ========================================= */

  const handleCreate = async () => {
    try {
      setSubmitting(true);

      await callApi(
        instance,
        accounts,
        "POST",
        "/api/admin/lookup-values",
        form,
      );

      showToast("Lookup created successfully");

      setShowModal(false);

      resetForm();

      loadData();
    } catch (err) {
      console.error(err);

      showToast(
        err?.response?.data?.message || "Failed to create lookup",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================================
     UPDATE
  ========================================= */

  const handleUpdate = async () => {
    try {
      setSubmitting(true);

      await callApi(
        instance,
        accounts,
        "PUT",
        `/api/admin/lookup-values/${selectedRow.lookup_id}`,
        {
          lookup_name: form.lookup_name,
          lookup_description: form.lookup_description,
          sort_order: form.sort_order,
          is_active: form.is_active,
        },
      );

      showToast("Lookup updated successfully");

      setShowModal(false);

      resetForm();

      loadData();
    } catch (err) {
      console.error(err);

      showToast(
        err?.response?.data?.message || "Failed to update lookup",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================================
     DELETE
  ========================================= */

  const handleDelete = useCallback(
    async (row) => {
      const confirmed = window.confirm(`Deactivate ${row.lookup_code}?`);

      if (!confirmed) return;

      try {
        await callApi(
          instance,
          accounts,
          "DELETE",
          `/api/admin/lookup-values/${row.lookup_id}`,
        );

        showToast("Lookup deactivated successfully");

        loadData();
      } catch (err) {
        console.error(err);

        showToast("Failed to deactivate lookup", "error");
      }
    },
    [instance, accounts, loadData],
  );

  /* =========================================
     EDIT HELPER
  ========================================= */

  const handleEdit = useCallback((r) => {
    setSelectedRow(r);
    setForm({
      lookup_category: r.lookup_category,
      lookup_code: r.lookup_code,
      lookup_name: r.lookup_name,
      lookup_description: r.lookup_description,
      sort_order: r.sort_order,
      is_active: r.is_active,
    });
    setIsEdit(true);
    setShowModal(true);
  }, []);

  /* =========================================
     RESPONSIVE CARD MAPPERS
  ========================================= */

  const mapLookupCard = useCallback(
    (r) => ({
      id: `lookup-${r.lookup_id}`,
      icon: <Tag size={16} />,
      tone: r.is_active ? "success" : "warning",
      title: r.lookup_name,
      subtitle: r.lookup_category,
      meta: [
        { label: "Code", value: r.lookup_code || "—" },
        { label: "Sort Order", value: r.sort_order ?? "—" },
        {
          label: "Updated",
          value: r.updated_at
            ? new Date(r.updated_at).toLocaleDateString()
            : "—",
        },
      ],
      badges: [
        {
          value: r.is_active ? "Active" : "Inactive",
          tone: r.is_active ? "success" : "warning",
        },
      ],
      actions: [
        {
          label: "Edit",
          icon: <Pencil size={14} />,
          onClick: () => handleEdit(r),
        },
        {
          label: "Deactivate",
          icon: <Trash2 size={14} />,
          variant: "danger",
          onClick: () => handleDelete(r),
        },
      ],
    }),
    [handleEdit, handleDelete],
  );

  const mapLookupDrawer = useCallback(
    (r) => ({
      title: r.lookup_name,
      fields: [
        { label: "Category", value: r.lookup_category || "—" },
        { label: "Code", value: r.lookup_code || "—" },
        { label: "Description", value: r.lookup_description || "—" },
        { label: "Sort Order", value: r.sort_order ?? "—" },
        { label: "Status", value: r.is_active ? "Active" : "Inactive" },
        {
          label: "Last Updated",
          value: r.updated_at
            ? new Date(r.updated_at).toLocaleDateString()
            : "—",
        },
      ],
      primaryAction: {
        label: "Edit Lookup",
        onClick: () => handleEdit(r),
      },
    }),
    [handleEdit],
  );

  const renderTable = useCallback(
    () => (
      <>
        <div style={styles.tableCard}>
          {loading ? (
            <div style={styles.empty}>Loading...</div>
          ) : (
            <ScrollableTable>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={styles.th}>CATEGORY</th>

                <th style={styles.th}>CODE</th>

                <th style={styles.th}>NAME</th>

                <th style={styles.th}>ORDER</th>

                <th style={styles.th}>STATUS</th>

                <th style={styles.th}>UPDATED</th>

                <th style={styles.thRight}>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="7" style={styles.empty}>
                    No records found
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.lookup_id} style={styles.row}>
                    <td style={styles.td}>{r.lookup_category}</td>

                    <td style={styles.td}>
                      <span style={styles.codeBadge}>{r.lookup_code}</span>
                    </td>

                    <td style={styles.td}>
                      <div style={styles.nameBlock}>
                        <div style={styles.lookupName}>{r.lookup_name}</div>

                        <div style={styles.lookupDesc}>
                          {r.lookup_description || "—"}
                        </div>
                      </div>
                    </td>

                    <td style={styles.td}>{r.sort_order}</td>

                    <td style={styles.td}>
                      {r.is_active ? (
                        <span style={styles.activeBadge}>● Active</span>
                      ) : (
                        <span style={styles.inactiveBadge}>● Inactive</span>
                      )}
                    </td>

                    <td style={styles.td}>
                      {r.updated_at
                        ? new Date(r.updated_at).toLocaleDateString()
                        : "—"}
                    </td>

                    <td style={styles.tdRight}>
                      <div style={styles.actionRow}>
                        <button
                          style={styles.editBtn}
                          onClick={() => handleEdit(r)}
                        >
                          Edit
                        </button>

                        <button
                          style={styles.deleteBtn}
                          onClick={() => handleDelete(r)}
                        >
                          Deactivate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </ScrollableTable>
        )}
        </div>

        {totalRecords > 0 && (
          <Pagination
            page={page}
            totalPages={Math.ceil(totalRecords / pageSize) || 1}
            totalRecords={totalRecords}
            pageSize={pageSize}
            currentPageCount={rows.length}
            onPageChange={setPage}
          />
        )}
      </>
    ),
    [rows, loading, page, totalRecords, pageSize, handleEdit, handleDelete],
  );

  /* =========================================
     UI
  ========================================= */

  return (
    <div className="page-container" style={styles.container}>
      <div
        style={{
          ...styles.header,
          ...(isNarrowToolbar ? styles.headerNarrow : null),
        }}
      >
        <div style={isNarrowToolbar ? styles.headerTitleBlock : undefined}>
          <h2 style={styles.title}>Lookup Management</h2>

          <p style={styles.sub}>
            Manage enterprise lookup categories and values.
          </p>
        </div>

        {isNarrowToolbar ? (
          <div style={styles.addBtnWrap}>
            <button
              type="button"
              style={{ ...styles.addBtn, ...styles.addBtnCompact }}
              aria-label="Add lookup"
              aria-describedby={isAddTooltipVisible ? "lookup-add-tooltip" : undefined}
              onMouseEnter={showAddTooltip}
              onMouseLeave={hideAddTooltip}
              onFocus={showAddTooltip}
              onBlur={hideAddTooltip}
              onTouchStart={handleAddTooltipTouchStart}
              onTouchEnd={handleAddTooltipTouchEnd}
              onClick={() => { resetForm(); setShowModal(true); }}
            >
              <ListPlus size={20} strokeWidth={2} aria-hidden />
            </button>
            {isAddTooltipVisible && (
              <div id="lookup-add-tooltip" style={styles.addTooltip} role="tooltip">
                <span style={styles.addTooltipArrow} aria-hidden />
                Add lookup
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            style={styles.addBtn}
            onClick={() => { resetForm(); setShowModal(true); }}
          >
            + Add Lookup
          </button>
        )}
      </div>

      {/* =====================================
          TOOLBAR
      ===================================== */}

      <div
        style={{
          ...styles.toolbar,
          ...(isNarrowToolbar ? styles.toolbarNarrow : null),
        }}
      >
        <select
          value={category}
          onChange={(e) => {
            setPage(1);
            setCategory(e.target.value);
          }}
          style={{
            ...styles.select,
            ...(isNarrowToolbar ? styles.inputNarrow : null),
          }}
        >
          {categories.map((c) => (
            <option key={c.lookup_category} value={c.lookup_category}>
              {c.lookup_category}
            </option>
          ))}
        </select>

        <input
          placeholder="Search lookup"
          style={{
            ...styles.search,
            ...(isNarrowToolbar ? styles.inputNarrow : null),
          }}
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />

      </div>

      {/* =====================================
          TABLE / CARDS
      ===================================== */}

      <ResponsiveTableCards
        data={rows}
        cardMapper={mapLookupCard}
        drawerMapper={mapLookupDrawer}
        renderTable={renderTable}
        loading={loading}
        emptyTitle="No records found"
        emptySub="Try adjusting your search or category filter."
        pagination={
          totalRecords > 0
            ? {
                page,
                totalPages: Math.ceil(totalRecords / pageSize) || 1,
                totalRecords,
                pageSize,
                currentPageCount: rows.length,
                onPageChange: setPage,
              }
            : undefined
        }
      />

      {/* =====================================
          MODAL
      ===================================== */}

      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3>{isEdit ? "Edit Lookup" : "Create Lookup"}</h3>

              <span
                style={styles.close}
                onClick={() => {
                  setShowModal(false);

                  resetForm();
                }}
              >
                ✕
              </span>
            </div>

            <label style={styles.label}>Category</label>

            <input
              style={{
                ...styles.input,
                background: isEdit ? "#f1f5f9" : "#fff",
              }}
              value={form.lookup_category}
              disabled={isEdit}
              onChange={(e) =>
                setForm({
                  ...form,
                  lookup_category: e.target.value,
                })
              }
            />

            <label style={styles.label}>Code</label>

            <input
              style={{
                ...styles.input,
                background: isEdit ? "#f1f5f9" : "#fff",
              }}
              value={form.lookup_code}
              disabled={isEdit}
              onChange={(e) =>
                setForm({
                  ...form,
                  lookup_code: e.target.value,
                })
              }
            />

            <label style={styles.label}>Name</label>

            <input
              style={styles.input}
              value={form.lookup_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  lookup_name: e.target.value,
                })
              }
            />

            <label style={styles.label}>Description</label>

            <textarea
              style={styles.textarea}
              value={form.lookup_description}
              onChange={(e) =>
                setForm({
                  ...form,
                  lookup_description: e.target.value,
                })
              }
            />

            <label style={styles.label}>Sort Order</label>

            <input
              type="number"
              style={styles.input}
              value={form.sort_order}
              onChange={(e) =>
                setForm({
                  ...form,
                  sort_order: e.target.value,
                })
              }
            />

            <div style={styles.toggleRow}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_active: e.target.checked,
                  })
                }
              />

              <span>Active</span>
            </div>

            <div style={styles.footer}>
              <button
                style={styles.cancelBtn}
                onClick={() => {
                  setShowModal(false);

                  resetForm();
                }}
              >
                Cancel
              </button>

              <button
                style={styles.primaryBtn}
                disabled={submitting}
                onClick={isEdit ? handleUpdate : handleCreate}
              >
                {submitting
                  ? "Saving..."
                  : isEdit
                    ? "Update Lookup"
                    : "Create Lookup"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "success" ? "#16a34a" : "#dc2626",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: "#f8fafc",
    minHeight: "100vh",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    marginTop: "6px",
    color: "#64748b",
  },

  addBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: "600",
  },

  toolbar: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    marginBottom: "20px",
    background: "#fff",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
  },

  select: {
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
  },

  search: {
    flex: 1,
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
  },

  count: {
    color: "#64748b",
    fontSize: "13px",
  },

  tableCard: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  thead: {
    background: "#f8fafc",
  },

  th: {
    padding: "14px 16px",
    textAlign: "left",
    fontSize: "12px",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
  },

  thRight: {
    padding: "14px 16px",
    textAlign: "right",
    fontSize: "12px",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
  },

  td: {
    padding: "16px",
    borderBottom: "1px solid #f1f5f9",
  },

  tdRight: {
    padding: "16px",
    textAlign: "right",
    borderBottom: "1px solid #f1f5f9",
  },

  row: {
    transition: "background 0.2s ease",
  },

  codeBadge: {
    background: "#eff6ff",
    color: "#2563eb",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  nameBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  lookupName: {
    fontWeight: "600",
    color: "#0f172a",
  },

  lookupDesc: {
    color: "#64748b",
    fontSize: "13px",
  },

  activeBadge: {
    color: "#15803d",
    fontWeight: "600",
  },

  inactiveBadge: {
    color: "#dc2626",
    fontWeight: "600",
  },

  actionRow: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
  },

  editBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
  },

  deleteBtn: {
    background: "#fee2e2",
    color: "#dc2626",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
  },

  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
  },

  pageBtn: {
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1px solid #dbe2ea",
    background: "#fff",
    cursor: "pointer",
  },

  pageText: {
    color: "#475569",
  },

  empty: {
    padding: "40px",
    textAlign: "center",
    color: "#64748b",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },

  modal: {
    background: "#fff",
    borderRadius: "18px",
    width: "100%",
    maxWidth: "560px",
    padding: "24px",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },

  close: {
    cursor: "pointer",
    fontSize: "18px",
  },

  label: {
    display: "block",
    marginTop: "14px",
    marginBottom: "6px",
    fontSize: "13px",
    fontWeight: "600",
  },

  input: {
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    boxSizing: "border-box",
  },

  textarea: {
    width: "100%",
    minHeight: "90px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    resize: "vertical",
    boxSizing: "border-box",
  },

  toggleRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "18px",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "28px",
  },

  cancelBtn: {
    border: "1px solid #dbe2ea",
    background: "#fff",
    padding: "10px 16px",
    borderRadius: "10px",
    cursor: "pointer",
  },

  primaryBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
  },

  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: "12px",
    fontWeight: "600",
    zIndex: 1000,
  },
};
